import { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import ReactSelect from 'react-select'
import AddIcon from '../../assets/AddIcon'
import BarcodeIcon from '../../assets/BarcodeIcon'
import CloseIcon from '../../assets/CloseIcon'
import ReturnBlack from '../../assets/ReturnBlack'
import SortIcon from '../../assets/SortIcon'
import {
  HEADER_CONFIG,
  HEADER_CONST,
} from '../../common/constant/common-constant'
import {
  PAGE_CONST,
  PERMISSION_FEATURE,
  PERMISSION_SCREEN,
  PERMISSION_TYPE,
  PRODUCT_CATEGORY,
  PRODUCT_DETAIL_FIELDS,
  STORE_RETURN_FIELDS,
  STORE_RETURN_REASON_OPTIONS,
  STORE_RETURN_REASON_LABEL,
} from '../../common/constant/page-constant'
import useCheckMobileScreen from '../../common/hooks/use-check-mobile-screen-hook'
import { formatDate } from '../../common/utils/appUtils'
import { redirect } from '../../common/utils/redirectionUtils'
import Alert from '../../components/Alert/Alert'
import BarcodeScanner from '../../components/ZXingScanner/BarcodeScanner'
import {
  addStoreReturnHelper,
  getStoreReturnsHelper,
  updateStoreReturnHelper,
  deleteStoreReturnHelper,
  getProductDetailsByBarcodeHelper,
} from '../../helper/home-helper'
import {
  getProductDetailByIdHelper,
  getProductSearchSuggestionsHelper,
} from '../../helper/personal-details-helper'
import { addStoreReturnAlertAction } from '../../modules/actions/action'
import Card from '../Common/Card/Card'
import MobileHeader from '../Common/Header/MobileHeader'
import PopupCard from '../Common/PopupCard/PopupCard'
import SideDrawer from '../Common/SideDrawer/SideDrawer'

import currencyFormatter from './../../common/utils/currencyFormatterUtils'
import './StoreReturn.scss'

const StoreReturn = (props) => {
  const navigate = useNavigate()
  const isMobile = useCheckMobileScreen()

  const [viewDrawer, setViewDrawer] = useState(false)
  const [open, setOpen] = useState(false)
  const [isSorting, setIsSorting] = useState(false)
  const [productData, setProductData] = useState(undefined)
  const [storeReturnData, setStoreReturnData] = useState(undefined)
  const [managedStoreReturnData, setManagedStoreReturnData] = useState(undefined)
  const [isFieldErrorVisible, setIsFieldErrorVisible] = useState(false)
  const [serverError, setServerError] = useState(undefined)
  const [addStoreReturnAlert, setAddStoreReturnAlert] = useState(false)
  const [headerOptions, setHeaderOptions] = useState({
    [HEADER_CONFIG.IS_BACK_VISIBLE]: true,
    [HEADER_CONFIG.TITLE]: HEADER_CONST.STORE_RETURN,
    [HEADER_CONFIG.HANDLE_BACK_FUNC]: () => redirect(navigate, PAGE_CONST.HOME),
  })

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSearchTerm, setSelectedSearchTerm] = useState(undefined)
  const [searchSuggestions, setSearchSuggestions] = useState([])
  const searchTimerRef = useRef(null)
  const searchAbortRef = useRef(null)
  const [isScannerVisible, setIsScannerVisible] = useState(false)
  const [permissions, setPermissions] = useState({})
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingItemId, setEditingItemId] = useState(undefined)
  const [deletePopup, setDeletePopup] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(undefined)

  useEffect(() => {
    let addStoreReturnAlert = props.alert?.addStoreReturnAlert
    setAddStoreReturnAlert(addStoreReturnAlert)
  }, [props.alert?.addStoreReturnAlert])

  useEffect(() => {
    getStoreReturnData()
  }, [])

  useEffect(() => {
    if (!open) {
      // Reset data when drawer closes
      resetDrawerData()
      handleHeaderConfigUpdate(HEADER_CONST.STORE_RETURN, () =>
        redirect(navigate, PAGE_CONST.HOME)
      )
      return
    }

    // Only reset data when drawer opens for adding (not editing)
    if (!isEditMode) {
      resetDrawerData()
      handleHeaderConfigUpdate(HEADER_CONST.ADD_STORE_RETURN, () =>
        handleDrawerToggle()
      )
    } else {
      handleHeaderConfigUpdate(HEADER_CONST.STORE_RETURN, () =>
        handleDrawerToggle()
      )
    }
  }, [open, isEditMode])

  useEffect(() => {
    handleSearchSuggestions(searchTerm)
    return () => clearTimeout(searchTimerRef.current)
  }, [searchTerm])

  useEffect(() => {
    if (selectedSearchTerm) {
      handleSearch(selectedSearchTerm)
    }
  }, [selectedSearchTerm])

  useEffect(() => {
    if (props.loginData.permissions) {
      let permission = props.loginData.permissions.filter(
        (permission) => permission.screen === PERMISSION_SCREEN.MANAGE_STORE
      )[0]

      if (permission) setPermissions(permission)
    }
  }, [props.loginData])

  const handleSearchSuggestions = (searchTerm) => {
    if (searchAbortRef.current) {
      searchAbortRef.current.abort()
    }
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }

    if (!searchTerm || searchTerm?.length === 0) {
      clearSearchData()
      return
    }

    searchTimerRef.current = setTimeout(() => {
      const abortController = new AbortController()
      searchAbortRef.current = abortController

      getProductSearchSuggestionsHelper(searchTerm, abortController.signal)
        .then((results) => {
          if (abortController.signal.aborted) return
          setSearchSuggestions(results)
        })
        .catch(() => {})
    }, 500)
  }

  const handleSearch = async () => {
    let data = await getProductDetailByIdHelper(selectedSearchTerm)
    setProductData(data)
    setStoreReturnData((storeReturnData) => {
      return {
        ...storeReturnData,
        [STORE_RETURN_FIELDS.BARCODE]:
          data?.[PRODUCT_DETAIL_FIELDS.PRODUCT_BARCODE],
      }
    })
  }

  const clearSearchData = () => {
    setSearchSuggestions([])
    setSelectedSearchTerm(undefined)
    if (searchAbortRef.current) searchAbortRef.current.abort()
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
  }

  const handleHeaderConfigUpdate = (title, handleBackFunc) => {
    setHeaderOptions({
      [HEADER_CONFIG.IS_BACK_VISIBLE]: true,
      [HEADER_CONFIG.TITLE]: title,
      [HEADER_CONFIG.HANDLE_BACK_FUNC]: () => handleBackFunc(),
    })
  }

  const getStoreReturnData = () => {
    getStoreReturnsHelper()
      .then((res) => {
        setManagedStoreReturnData(res)
      })
      .catch((err) => {
        console.log(err)
      })
  }

  const handleDrawerToggle = () => {
    setViewDrawer(!viewDrawer)
    setOpen(!open)
  }

  const getProductDetailsByBarcode = (bCode) => {
    setServerError('')

    let barCode = bCode || storeReturnData?.[STORE_RETURN_FIELDS.BARCODE]
    if (!barCode) {
      return
    }
    getProductDetailsByBarcodeHelper(barCode)
      .then((res) => {
        setProductData(undefined)
        if (res) setProductData(res)
      })
      .catch((err) => {
        setServerError(err)
        setProductData(undefined)
        setStoreReturnData({
          ...storeReturnData,
          [PRODUCT_DETAIL_FIELDS.PRODUCT_BARCODE]: barCode,
          [PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS]: undefined,
          product_batch_inventory_id: undefined,
        })
      })
  }

  const handleStoreReturnDetailUpdate = (key, value) => {
    setStoreReturnData((storeReturnData) => {
      return {
        ...storeReturnData,
        [key]: value,
      }
    })
  }

  const checkValidation = () => {
    if (
      storeReturnData?.[STORE_RETURN_FIELDS.CHALLAN_NUMBER] &&
      storeReturnData?.[STORE_RETURN_FIELDS.BARCODE] &&
      storeReturnData?.[STORE_RETURN_FIELDS.QUANTITY] &&
      storeReturnData?.[STORE_RETURN_FIELDS.REASON] &&
      storeReturnData?.[STORE_RETURN_FIELDS.NOTES] &&
      storeReturnData?.product_batch_inventory_id &&
      parseInt(storeReturnData?.[PRODUCT_DETAIL_FIELDS.QUANTITY]) <=
        parseInt(
          storeReturnData?.[PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS]?.[
            PRODUCT_DETAIL_FIELDS.STOCK
          ]
        )
    ) {
      return true
    }

    return false
  }

  const resetDrawerData = () => {
    setStoreReturnData(undefined)
    setProductData(undefined)
    setServerError(undefined)
    setIsFieldErrorVisible(false)
    setSearchTerm('')
    setSelectedSearchTerm(undefined)
    setSearchSuggestions([])
    setIsEditMode(false)
    setEditingItemId(undefined)
  }

  const handleScan = (result) => {
    if (result) {
      handleStoreReturnDetailUpdate(
        PRODUCT_DETAIL_FIELDS.PRODUCT_BARCODE,
        result
      )
      setIsScannerVisible(false)
      getProductDetailsByBarcode(result)
    }
  }

  const handleBarcodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      getProductDetailsByBarcode()
    }
  }

  const handleCardAction = (item, action) => {
    if (action === 'EDIT') {
      handleEditItem(item)
    } else if (action === 'DELETE') {
      setItemToDelete(item)
      setDeletePopup(true)
    }
  }

  const handleEditItem = (item) => {
    // Clear previous state
    setServerError(undefined)
    setIsFieldErrorVisible(false)
    setSearchTerm('')
    setSelectedSearchTerm(undefined)
    setSearchSuggestions([])
    
    // Set edit mode
    setIsEditMode(true)
    setEditingItemId(item.id)
    
    // Build batch details object for the dropdown
    const batchDetails = {
      product_batch_inventory_id: item.product_batch_inventory_id,
      [PRODUCT_DETAIL_FIELDS.BATCH_NUMBER]: item.batch_number,
      [PRODUCT_DETAIL_FIELDS.EXPIRY_DATE]: item.expiry_date,
      [PRODUCT_DETAIL_FIELDS.PRODUCT_COST]: item.cost_per_unit,
      [PRODUCT_DETAIL_FIELDS.STOCK]: item.stock,
      label: item.batch_number ? `${item.batch_number}${item.expiry_date ? ` - ${item.expiry_date}` : ''}` : 'Batch',
    }
    
    // Populate the form with existing data
    setStoreReturnData({
      [STORE_RETURN_FIELDS.CHALLAN_NUMBER]: item.challan_number,
      [STORE_RETURN_FIELDS.BARCODE]: item.barcode,
      [STORE_RETURN_FIELDS.QUANTITY]: item.quantity,
      [STORE_RETURN_FIELDS.REASON]: item.reason,
      [STORE_RETURN_FIELDS.NOTES]: item.notes,
      product_batch_inventory_id: item.product_batch_inventory_id,
      [PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS]: batchDetails,
    })
    
    setProductData({
      product_id: item.product_id,
      [PRODUCT_DETAIL_FIELDS.PRODUCT_NAME]: item.product_name,
      [PRODUCT_DETAIL_FIELDS.DOSE]: item.dose,
      [PRODUCT_DETAIL_FIELDS.PRODUCT_CATEGORY]: item.product_type,
      strength: item.strength,
      category: item.category,
      cost_per_unit: item.cost_per_unit,
      [PRODUCT_DETAIL_FIELDS.PRODUCT_NEW_COST]: item.cost_per_unit,
      inventory: item.inventory || [],
    })
    
    setOpen(true)
  }

  const handleDeleteItem = () => {
    if (!itemToDelete) return
    
    deleteStoreReturnHelper(itemToDelete.id)
      .then(() => {
        setDeletePopup(false)
        setItemToDelete(undefined)
        getStoreReturnData()
      })
      .catch((err) => {
        setServerError(err)
        setDeletePopup(false)
      })
  }

  const handleSubmit = () => {
    if (!checkValidation()) {
      setIsFieldErrorVisible(true)
      return
    }

    setIsFieldErrorVisible(false)

    let data = {
      storeReturnData,
      productData,
    }

    if (isEditMode && editingItemId) {
      // Update existing record
      updateStoreReturnHelper({ ...data, id: editingItemId })
        .then((res) => {
          props.addStoreReturnAlertAction(true)
          setTimeout(() => {
            props.addStoreReturnAlertAction(false)
          }, 5000)
          getStoreReturnData()
          resetDrawerData()
          setIsEditMode(false)
          setEditingItemId(undefined)
          handleDrawerToggle()
        })
        .catch((err) => {
          setServerError(err)
        })
    } else {
      // Create new record
      addStoreReturnHelper(data)
        .then((res) => {
          props.addStoreReturnAlertAction(true)
          setTimeout(() => {
            props.addStoreReturnAlertAction(false)
          }, 5000)
          getStoreReturnData()
          resetDrawerData()
          handleDrawerToggle()
        })
        .catch((err) => {
          setServerError(err)
        })
    }
  }

  return (
    <>
      {isMobile && <MobileHeader headerOptions={headerOptions} />}
      {addStoreReturnAlert && (
        <Alert
          success
          alertType="success"
          alertMessage="New Store Return added Successfully!"
        />
      )}
      <div className="pageBodyRightPanel StoreReturn">
        {isMobile &&
          permissions?.features?.[PERMISSION_FEATURE.STORE_RETURN] ===
            PERMISSION_TYPE.MANAGE && (
            <div className="addProductBtnMobile dFlex flexColumnReverse aiCenter mwebVisible fixed">
              <button
                className="animated zoomIn btn btnPrimary"
                onClick={handleDrawerToggle}
              >
                <AddIcon />
              </button>
              <button
                className="animated zoomIn btn btnSecondary pointer mb-2"
                onClick={() => setIsSorting(true)}
              >
                <SortIcon />
              </button>
            </div>
          )}
        {!isMobile && (
          <div className="pageBodyRightPanelHeader dFlex jcSpaceBetween aiCenter">
            <div className="pageBodyRightPanelHeading">
              Store Return
            </div>
            <div className="addProductBtn dFlex aiCenter">
              {permissions?.features?.[PERMISSION_FEATURE.STORE_RETURN] ===
                PERMISSION_TYPE.MANAGE && (
                <button className="btn btnPrimary" onClick={handleDrawerToggle}>
                  ADD
                </button>
              )}
              <div className="pointer ml-4" onClick={() => setIsSorting(true)}>
                <SortIcon />
              </div>
              <div className="pointer ml-4" onClick={() => props.onClose()}>
                <CloseIcon />
              </div>
            </div>
          </div>
        )}
        <div className="productLlistWrap dFlex">
          {managedStoreReturnData &&
            managedStoreReturnData.map((storeReturnItem) => {
              return (
                <div className="cardWrap" key={storeReturnItem.id}>
                  <Card
                    cardCrown={STORE_RETURN_REASON_LABEL[storeReturnItem.reason] || storeReturnItem.reason}
                    product={{
                      productName: `${storeReturnItem.product_name}${storeReturnItem.dose ? ` (${storeReturnItem.dose})` : ''}${storeReturnItem.strength ? `-${storeReturnItem.strength}` : ''}${storeReturnItem.category ? `-${storeReturnItem.category}` : ''}`,
                      quantity: storeReturnItem.quantity,
                    }}
                    productDesc={storeReturnItem?.notes}
                    headerSup={formatDate(storeReturnItem?.created_at)}
                    headerMain={storeReturnItem.challan_number}
                    cardRight={
                      storeReturnItem
                        ? `BDT ${currencyFormatter(storeReturnItem.amount || 0)}`
                        : '---'
                    }
                    users={permissions?.features?.[PERMISSION_FEATURE.STORE_RETURN] === PERMISSION_TYPE.MANAGE}
                    cardAction={['EDIT', 'DELETE']}
                    onCardAction={(action) => handleCardAction(storeReturnItem, action)}
                  />
                </div>
              )
            })}
          {(!managedStoreReturnData || managedStoreReturnData.length === 0) && (
            <div className="noData">No Store Returns Found</div>
          )}
        </div>
        {/* Delete Confirmation Popup */}
        {deletePopup && (
          <div className="popupOverlay">
            <PopupCard
              header="Delete Store Return?"
              body="Are you sure you want to delete this store return record? This action cannot be undone."
            >
              <button
                className="btn btnPrimaryOutlined dFlex"
                onClick={() => {
                  setDeletePopup(false)
                  setItemToDelete(undefined)
                }}
              >
                Cancel
              </button>
              <button
                className="btn btnPrimary dFlex"
                onClick={handleDeleteItem}
              >
                Yes, Delete
              </button>
            </PopupCard>
          </div>
        )}
        {/* side drawer for filter and sorting */}
        <SideDrawer
          handleDrawerToggle={() => setIsSorting(!isSorting)}
          open={isSorting}
          drawerButton={true}
          drawerHeading="Sort"
          mwebSideDrawerFooter
        >
          <form className="mt-5">
            <div className="formGroup formFloating">
              <select className="formSelect" id="floatingSelect">
                <option value="none" selected disabled hidden>
                  Quantity
                </option>
                <option value="1">1</option>
                <option value="2">2</option>
              </select>
              <label htmlFor="floatingSelect">Quantity</label>
            </div>
            <div className="formGroup formFloating">
              <select className="formSelect" id="floatingSelect">
                <option value="none" selected disabled hidden>
                  Amount
                </option>
                <option value="1">1</option>
                <option value="2">2</option>
              </select>
              <label htmlFor="floatingSelect">Amount</label>
            </div>
          </form>
        </SideDrawer>
        <SideDrawer
          handleDrawerToggle={handleDrawerToggle}
          open={open}
          drawerButton={true}
          drawerHeading={isEditMode ? "Edit Store Return" : "Add Store Return"}
          handleClick={() => handleSubmit()}
        >
          <div className="wizardFieldset">
            {serverError && (
              <div className="invalidFeedback mb-4">{serverError}</div>
            )}
            <div className="consolidateStrip dFlex jcSpaceBetween aiCenter mb-5">
              <div className="consolidatedDataIcon dFlex">
                <ReturnBlack />
              </div>
              <div className="consolidateDataText">
                Store Return Amount <small>BDT</small>
              </div>
              <div className="consolidateData">
                {productData &&
                productData.cost_per_unit &&
                storeReturnData?.[STORE_RETURN_FIELDS.QUANTITY]
                  ? productData?.[PRODUCT_DETAIL_FIELDS.PRODUCT_NEW_COST] *
                    storeReturnData?.[STORE_RETURN_FIELDS.QUANTITY]
                  : '---'}
              </div>
            </div>
            <form>
              <div className="formGroup formFloating fgrow1">
                <input
                  type="text"
                  className="formControl"
                  id="challan"
                  placeholder="Challan Number"
                  value={storeReturnData?.[STORE_RETURN_FIELDS.CHALLAN_NUMBER]}
                  onChange={(e) =>
                    handleStoreReturnDetailUpdate(
                      STORE_RETURN_FIELDS.CHALLAN_NUMBER,
                      e.target.value
                    )
                  }
                  required
                />
                <label htmlFor="challan">Challan Number</label>
                {isFieldErrorVisible &&
                  !storeReturnData?.[STORE_RETURN_FIELDS.CHALLAN_NUMBER] && (
                    <div className="invalidFeedback">
                      Please enter a Challan Number.
                    </div>
                  )}
              </div>
              <div className="formSection">
                <div className="formGroup formFloating fgrow1">
                  <input
                    type="text"
                    className="formControl"
                    id="barcode"
                    placeholder="Product Barcode/QR Code"
                    value={storeReturnData?.[STORE_RETURN_FIELDS.BARCODE] || ''}
                    onChange={(e) =>
                      handleStoreReturnDetailUpdate(
                        STORE_RETURN_FIELDS.BARCODE,
                        e.target.value
                      )
                    }
                    onBlur={(e) => getProductDetailsByBarcode()}
                    onKeyDown={handleBarcodeKeyDown}
                    required
                  />
                  <label htmlFor="barcode">Product Barcode/QR Code</label>
                  {isFieldErrorVisible &&
                    (!storeReturnData?.[STORE_RETURN_FIELDS.BARCODE] ||
                      !productData?.product_id) && (
                      <div className="invalidFeedback">
                        Please enter a valid Product Barcode/QR Code.
                      </div>
                    )}
                  <div
                    className="positionedIcon"
                    onClick={() => setIsScannerVisible(!isScannerVisible)}
                  >
                    <BarcodeIcon />
                  </div>
                  {isScannerVisible && <BarcodeScanner onScan={handleScan} />}
                </div>
              </div>
              <div className="formGroup formFloating">
                <ReactSelect
                  className="formSelect reactSelect"
                  id="productSelect"
                  value={{
                    label: productData ? `${productData?.[PRODUCT_DETAIL_FIELDS.PRODUCT_NAME] || ''}${productData?.[PRODUCT_DETAIL_FIELDS.DOSE] ? ` (${productData[PRODUCT_DETAIL_FIELDS.DOSE]})` : ''}${productData?.strength ? `-${productData.strength}` : ''}${productData?.category ? `-${productData.category}` : ''}` : '',
                  }}
                  onInputChange={(option) => {
                    if (option) setSearchTerm(option)
                  }}
                  onChange={(option) => {
                    setSelectedSearchTerm(option?.id)
                  }}
                  options={searchSuggestions}
                  getOptionLabel={(option) => `${option.label || option.name || ''}${option.dose ? ` (${option.dose})` : ''}${option.strength ? `-${option.strength}` : ''}${option.category ? `-${option.category}` : ''}`}
                  placeholder="Product Name"
                  styles={{
                    control: (base) => ({
                      border: 0,
                      display: 'flex',
                      paddingTop: '20px',
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused
                        ? 'rgb(222, 235, 255)'
                        : state.isSelected
                          ? '#FFF'
                          : 'inherit',
                      color: '#212529',
                      '&:hover': {
                        backgroundColor: state.isSelected
                          ? '#DEEBFF'
                          : 'rgb(222, 235, 255)',
                      },
                    }),
                  }}
                />
                <label htmlFor="productSelect">Product Name</label>
                {isFieldErrorVisible &&
                  !productData?.[PRODUCT_DETAIL_FIELDS.PRODUCT_NAME] && (
                    <div className="invalidFeedback">Please select Product Name.</div>
                  )}
              </div>
              <div className="formGroup formFloating">
                <ReactSelect
                  className="formSelect reactSelect"
                  id="batchSelect"
                  value={
                    storeReturnData?.[
                      PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS
                    ] || ''
                  }
                  onChange={(option) => {
                    handleStoreReturnDetailUpdate(
                      'product_batch_inventory_id',
                      option?.product_batch_inventory_id
                    )
                    handleStoreReturnDetailUpdate(
                      PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS,
                      option
                    )
                  }}
                  options={productData?.inventory || []}
                  getOptionLabel={(option) =>
                    `Batch: ${option?.[PRODUCT_DETAIL_FIELDS.BATCH_NUMBER]}, 
                    Expiry: ${formatDate(option?.[PRODUCT_DETAIL_FIELDS.EXPIRY_DATE])}`
                  }
                  placeholder="Select Product Batch"
                  styles={{
                    control: (base) => ({
                      border: 0,
                      display: 'flex',
                      paddingTop: '20px',
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused
                        ? 'rgb(222, 235, 255)'
                        : state.isSelected
                          ? '#FFF'
                          : 'inherit',
                      color: '#212529',
                      '&:hover': {
                        backgroundColor: state.isSelected
                          ? '#DEEBFF'
                          : 'rgb(222, 235, 255)',
                      },
                    }),
                  }}
                />
                <label htmlFor="batchSelect">Select Product Batch</label>
                {isFieldErrorVisible &&
                  !storeReturnData?.[
                    PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS
                  ]?.product_batch_inventory_id && (
                    <div className="invalidFeedback">
                      Please Select Product Batch.
                    </div>
                  )}
                {storeReturnData?.[
                  PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS
                ] && (
                  <div className="inputSuggestionsWrap">
                    <ul className="inputSuggestion dFlex">
                      <li className="suggestion">
                        Selected Batch Stock:{' '}
                        {
                          storeReturnData?.[
                            PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS
                          ]?.[PRODUCT_DETAIL_FIELDS.STOCK]
                        }
                      </li>
                    </ul>
                  </div>
                )}
              </div>
              <div className="formInline dFlex flexNowrap">
                <div className="formGroup formFloating fgrow1">
                  <input
                    type="text"
                    className="formControl"
                    id="quantity"
                    placeholder="Quantity"
                    value={storeReturnData?.[STORE_RETURN_FIELDS.QUANTITY]}
                    onChange={(e) =>
                      handleStoreReturnDetailUpdate(
                        STORE_RETURN_FIELDS.QUANTITY,
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="quantity">Quantity</label>
                  {isFieldErrorVisible &&
                    (!storeReturnData?.[STORE_RETURN_FIELDS.QUANTITY] ||
                      parseInt(
                        storeReturnData?.[PRODUCT_DETAIL_FIELDS.QUANTITY]
                      ) >
                        parseInt(
                          storeReturnData?.[
                            PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS
                          ]?.[PRODUCT_DETAIL_FIELDS.STOCK]
                        )) && (
                      <div className="invalidFeedback">
                        Please select a valid Quantity.
                      </div>
                    )}
                </div>
                <div className="formGroup formFloating ml-4 fgrow1">
                  <input
                    type="text"
                    className="formControl"
                    id="dose"
                    placeholder="Dose"
                    value={
                      productData
                        ? productData?.[PRODUCT_DETAIL_FIELDS.DOSE]
                        : ''
                    }
                    disabled={true}
                    required
                  />
                  <label htmlFor="dose">Dose</label>
                </div>
              </div>
              <div className="formSection dFlex mb-5">
                <div className="formCheck">
                  <input
                    className="formCheckInput"
                    type="radio"
                    name="productCategory"
                    id="Pharmaceutical"
                    disabled={true}
                    checked={
                      productData?.[PRODUCT_DETAIL_FIELDS.PRODUCT_CATEGORY] ===
                      PRODUCT_CATEGORY.PHARMACEUTICAL
                    }
                  />
                  <label className="formCheckLabel" htmlFor="Pharmaceutical">
                    Pharmaceutical
                  </label>
                </div>
                <div className="formCheck ml-5">
                  <input
                    className="formCheckInput"
                    type="radio"
                    name="productCategory"
                    id="nPharmaceutical"
                    disabled={true}
                    checked={
                      productData?.[PRODUCT_DETAIL_FIELDS.PRODUCT_CATEGORY] ===
                      PRODUCT_CATEGORY.NON_PHARMACEUTICAL
                    }
                  />
                  <label className="formCheckLabel" htmlFor="nPharmaceutical">
                    Non-Pharmaceutical
                  </label>
                </div>
              </div>
              <div className="formGroup formFloating formInputText">
                <div className="preInputGroup backColor">
                  <div className="preInputText">BDT</div>
                </div>
                <input
                  type="text"
                  className="formControl"
                  id="cost_unit"
                  placeholder="Cost/Unit"
                  value={
                    storeReturnData?.[
                      PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS
                    ]?.[PRODUCT_DETAIL_FIELDS.PRODUCT_COST] || ''
                  }
                  disabled={true}
                  required
                />
                <label htmlFor="cost_unit">Cost/Unit</label>
              </div>
              <div className="formGroup formFloating fgrow1">
                <ReactSelect
                  className="formSelect reactSelect"
                  id="reasonSelect"
                  value={STORE_RETURN_REASON_OPTIONS.find(
                    (opt) => opt.value === storeReturnData?.[STORE_RETURN_FIELDS.REASON]
                  )}
                  onChange={(option) =>
                    handleStoreReturnDetailUpdate(
                      STORE_RETURN_FIELDS.REASON,
                      option?.value
                    )
                  }
                  options={STORE_RETURN_REASON_OPTIONS}
                  getOptionLabel={(option) => option.label}
                  placeholder="Reason for Return"
                  styles={{
                    control: (base) => ({
                      border: 0,
                      display: 'flex',
                      paddingTop: '20px',
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused
                        ? 'rgb(222, 235, 255)'
                        : state.isSelected
                          ? '#FFF'
                          : 'inherit',
                      color: '#212529',
                      '&:hover': {
                        backgroundColor: state.isSelected
                          ? '#DEEBFF'
                          : 'rgb(222, 235, 255)',
                      },
                    }),
                  }}
                  isClearable
                />
                <label htmlFor="reasonSelect">Reason for Return</label>
                {isFieldErrorVisible &&
                  !storeReturnData?.[STORE_RETURN_FIELDS.REASON] && (
                    <div className="invalidFeedback">
                      Please select a Reason for Return.
                    </div>
                  )}
              </div>
              <div className="formGroup formFloating">
                <textarea
                  rows="2"
                  type="text"
                  className="formControl"
                  id="notes"
                  value={storeReturnData?.[STORE_RETURN_FIELDS.NOTES]}
                  onChange={(e) =>
                    handleStoreReturnDetailUpdate(
                      STORE_RETURN_FIELDS.NOTES,
                      e.target.value
                    )
                  }
                  required
                ></textarea>
                <label htmlFor="notes">Notes</label>
                {isFieldErrorVisible &&
                  !storeReturnData?.[STORE_RETURN_FIELDS.NOTES] && (
                    <div className="invalidFeedback">Please enter notes.</div>
                  )}
              </div>
            </form>
          </div>
        </SideDrawer>
      </div>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    alert: state.alertReducer.alert,
    loginData: state.loginDataReducer?.loginData,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    addStoreReturnAlertAction: (payload) =>
      dispatch(addStoreReturnAlertAction(payload)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(StoreReturn)

